import { StateCreator } from 'zustand';

import { DEFAULT_AUTH_PATH } from '../routeStore/constants/authPath';
import { SignOutOptions } from './types/SignOutOptions';
import { SignOutState } from './types/SignOutState';
import { SignOutStateDependencies } from './types/SignOutStateDependencies';

export const signOutStore =
  ({
    resetAllStores,
    useApiStore,
    useRouteStore,
  }: SignOutStateDependencies): StateCreator<SignOutState> =>
  (set, get) => ({
    isSigningOut: false,
    signOutMessage: null,

    computed: {
      get isSignOutRestricted(): boolean {
        const { pathname } = window.location;

        // The /mail and /preview flows allow users to progress through steps while holding a valid token.
        // A 401 received on these routes (ex: wrong PIN entry) must not trigger a sign-out, because doing so
        // would call resetAllStores(), wiping state like pinPurpose and leadChannelCode. That causes route guards
        // to redirect users back to /mail with an empty store, resulting in broken or incorrect API calls.
        const restrictedPaths = ['/mail', '/preview'];

        return restrictedPaths.some((path): boolean => pathname.startsWith(path));
      },
    },

    signOut: (options?: SignOutOptions): Promise<void> => {
      const {
        computed: { isSignOutRestricted },
        isSigningOut,
      } = get();

      if (isSigningOut || isSignOutRestricted) {
        return;
      }

      const { setAuthRedirectPath } = useRouteStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({ isSigningOut: true });

      return portalHttpRequest
        .delete('/2.0/session')
        .catch((): void => {
          // ignore for now..
        })
        .finally((): void => {
          // reset all stores to their initial state
          resetAllStores();

          // set the sign out message to display on the sign out page
          set({
            isSigningOut: false,
            signOutMessage: options?.withMessage,
          });

          // set the auth redirect path to where we'd want the user to go after signing back in.
          // if not provided, the user will be redirected to the default auth path after sign in.
          setAuthRedirectPath(
            options?.withAuthRedirectPath || DEFAULT_AUTH_PATH,
          );
        });
    },
  });
